// Icons: one SVG file per icon in ./svg (source of truth, exported from Figma 37:205).
// vite-plugin-svgr turns `*.svg?react` into React components. Mono icons use currentColor;
// Brick keeps its own terracotta colors (Figma: 색 오버라이드 금지).
import type { ComponentType, SVGProps } from "react";

import ArrowClockwiseCircularLine from "./svg/ArrowClockwiseCircularLine.svg?react";
import ArrowUpRightLine from "./svg/ArrowUpRightLine.svg?react";
import BoltFill from "./svg/BoltFill.svg?react";
import Brick from "./svg/Brick.svg?react";
import ChatFill from "./svg/ChatFill.svg?react";
import ChatLine from "./svg/ChatLine.svg?react";
import CheckmarkLine from "./svg/CheckmarkLine.svg?react";
import ChevronLeftLine from "./svg/ChevronLeftLine.svg?react";
import ChevronRightLine from "./svg/ChevronRightLine.svg?react";
import Dot3HorizontalLine from "./svg/Dot3HorizontalLine.svg?react";
import GearLine from "./svg/GearLine.svg?react";
import HouseFill from "./svg/HouseFill.svg?react";
import HouseLine from "./svg/HouseLine.svg?react";
import LockFill from "./svg/LockFill.svg?react";
import MagnifyingglassLine from "./svg/MagnifyingglassLine.svg?react";
import PaperplaneFill from "./svg/PaperplaneFill.svg?react";
import PencilLine from "./svg/PencilLine.svg?react";
import PersonFill from "./svg/PersonFill.svg?react";
import PersonLine from "./svg/PersonLine.svg?react";
import PictureLine from "./svg/PictureLine.svg?react";
import PlusLine from "./svg/PlusLine.svg?react";
import ReportLine from "./svg/ReportLine.svg?react";
import Sparkle2Fill from "./svg/Sparkle2Fill.svg?react";
import Square2StackedLine from "./svg/Square2StackedLine.svg?react";
import TicketLine from "./svg/TicketLine.svg?react";
import TrashcanLine from "./svg/TrashcanLine.svg?react";
import XmarkLine from "./svg/XmarkLine.svg?react";

export const icons = {
  ArrowClockwiseCircularLine,
  ArrowUpRightLine,
  BoltFill,
  Brick,
  ChatFill,
  ChatLine,
  CheckmarkLine,
  ChevronLeftLine,
  ChevronRightLine,
  Dot3HorizontalLine,
  GearLine,
  HouseFill,
  HouseLine,
  LockFill,
  MagnifyingglassLine,
  PaperplaneFill,
  PencilLine,
  PersonFill,
  PersonLine,
  PictureLine,
  PlusLine,
  ReportLine,
  Sparkle2Fill,
  Square2StackedLine,
  TicketLine,
  TrashcanLine,
  XmarkLine,
} satisfies Record<string, ComponentType<SVGProps<SVGSVGElement>>>;

export type IconName = keyof typeof icons;
export const iconNames = Object.keys(icons) as IconName[];

export type IconProps = Omit<SVGProps<SVGSVGElement>, "name"> & { size?: number | string };

/** `<Icon name="Brick" size={18} />`. Mono icons inherit CSS `color`. */
export function Icon({ name, size = 24, ...rest }: IconProps & { name: IconName }) {
  const Cmp = icons[name];
  return <Cmp width={size} height={size} aria-hidden {...rest} />;
}

// Named components for direct import: <IconBrick size={14} />
const named = (Cmp: ComponentType<SVGProps<SVGSVGElement>>) =>
  function NamedIcon({ size = 24, ...rest }: IconProps) {
    return <Cmp width={size} height={size} aria-hidden {...rest} />;
  };

export const IconArrowClockwiseCircularLine = named(ArrowClockwiseCircularLine);
export const IconArrowUpRightLine = named(ArrowUpRightLine);
export const IconBoltFill = named(BoltFill);
export const IconBrick = named(Brick);
export const IconChatFill = named(ChatFill);
export const IconChatLine = named(ChatLine);
export const IconCheckmarkLine = named(CheckmarkLine);
export const IconChevronLeftLine = named(ChevronLeftLine);
export const IconChevronRightLine = named(ChevronRightLine);
export const IconDot3HorizontalLine = named(Dot3HorizontalLine);
export const IconGearLine = named(GearLine);
export const IconHouseFill = named(HouseFill);
export const IconHouseLine = named(HouseLine);
export const IconLockFill = named(LockFill);
export const IconMagnifyingglassLine = named(MagnifyingglassLine);
export const IconPaperplaneFill = named(PaperplaneFill);
export const IconPencilLine = named(PencilLine);
export const IconPersonFill = named(PersonFill);
export const IconPersonLine = named(PersonLine);
export const IconPictureLine = named(PictureLine);
export const IconPlusLine = named(PlusLine);
export const IconReportLine = named(ReportLine);
export const IconSparkle2Fill = named(Sparkle2Fill);
export const IconSquare2StackedLine = named(Square2StackedLine);
export const IconTicketLine = named(TicketLine);
export const IconTrashcanLine = named(TrashcanLine);
export const IconXmarkLine = named(XmarkLine);
